import {
  Text,
  Popover,
  TextInput,
  Flex,
  Button,
} from '@mantine/core';
import { AxiosError } from 'axios';
import { useState } from 'react';

import { useMutationUpdateCustomer } from '@flecto/core/saas';
import { useTranslation } from '@flecto/translations'
import { type CoreCustomer, type ParameterID } from '@flecto/types';
import { useNotifications } from '@flecto/ui';
import { useHandleAxiosError } from '@flecto/utils';

type CustomerEmailInputProps = {
  customerId: ParameterID;
  buttonTextColor?: string;
  onUpdateSuccess?: (data: CoreCustomer) => void;

};

export const CustomerEmailInput = ({
  customerId,
  onUpdateSuccess,
  buttonTextColor = 'gray.7',
}: CustomerEmailInputProps) => {
  const [email, setEmail] = useState('');
  const { showNotification } = useNotifications();
  const { getErrorMessages } = useHandleAxiosError();
  const [popoverOpened, setPopoverOpened] = useState(false);
  const { t } = useTranslation('saas', { keyPrefix: 'booking_customer' });
  const { mutateAsync: updateCustomer, isLoading } = useMutationUpdateCustomer();

  const handleSaveEmail = async () => {
    try {
      if (email) {
        const response = await updateCustomer({
          customerId,
          customer: {
            email,
          },
        });
        setPopoverOpened(false);
        onUpdateSuccess?.(response);
      }
    } catch (error) {
      console.error(error);
      if (error instanceof AxiosError) {
        const errorMessages = getErrorMessages(error);
        showNotification({
          type: 'error',
          message: errorMessages[0].errorMessage,
        });
      }
    }
  };

  return (
    <Text
      size="sm"
      c={buttonTextColor}
      onClick={(e) => e.stopPropagation()}
    >
      {t('card_notification_without_email_description')}
      <Popover
        opened={popoverOpened}
        position="top"
        withArrow
        withinPortal
        zIndex={9999}
      >
        <Popover.Target>
          <Text
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
            ml={'xs'}
            fw="500"
            component="span"
            onClick={() => setPopoverOpened(true)}
          >
            {t('card_notification_badge_button')}
          </Text>
        </Popover.Target>
        <Popover.Dropdown miw={230}>
          <Flex direction="column" gap="sm">
            <Text size="sm" fw={500} mb="sm">
              {t('card_notification_badge_title')}
            </Text>
            <TextInput
              mb="sm"
              type='email'
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('card_notification_badge_input_placeholder')}
            />
            <Flex justify="space-between" gap="sm">
              <Button
                onClick={() => setPopoverOpened(false)}
                variant="default"
                c='text.color'
                w="100%"
              >
                {t('card_notification_badge_button_cancel')}
              </Button>
              <Button
                onClick={() => handleSaveEmail()}
                loading={isLoading}
                w="100%"
              >
                {t('card_notification_badge_button_save')}
              </Button>
            </Flex>
          </Flex>
        </Popover.Dropdown>
      </Popover>
      .
    </Text>
  );
};
